import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// Silence opaque "Script error." events on the CRA dev preview.
//
// "Script error." with no filename, line, column or Error object is the
// browser's placeholder for a genuinely cross-origin error — a browser
// extension, a third-party pixel/tag on a shared preview host, or an
// aborted network resource whose stack the browser refuses to expose for
// security reasons. It is not from our own code (same-origin errors always
// come through with real details). CRA's dev-server overlay flashes these
// as a red "Script error." banner anyway, which reads on the preview site
// as an app bug even though nothing in the app has actually failed. This
// filter drops ONLY that specific opaque form — anything with a filename,
// message or Error object still surfaces to React's ErrorBoundary and the
// dev overlay untouched.
const _isOpaqueScriptError = (message, source, lineno, error) => {
  const opaqueMsg = message === "Script error." || message === "Script error";
  const noOrigin = !source && !error && (!lineno || lineno === 0);
  return opaqueMsg && noOrigin;
};

// The CRA dev overlay hooks the legacy `window.onerror`, so we must wrap
// that too — `addEventListener` + `preventDefault` is not enough on its own.
const _prevOnError = window.onerror;
window.onerror = function (message, source, lineno, colno, error) {
  if (_isOpaqueScriptError(message, source, lineno, error)) {
    return true; // signal "handled" so the overlay skips it
  }
  return _prevOnError ? _prevOnError.apply(this, arguments) : false;
};

// Belt-and-braces for the modern event path, in case another listener
// (analytics, error reporter) also inspects it.
window.addEventListener("error", (event) => {
  if (_isOpaqueScriptError(event?.message, event?.filename, event?.lineno, event?.error)) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}, true);

// Same treatment for unhandled promise rejections whose reason is opaque —
// these are almost always AbortError from a fetch cancelled by React on
// route change or fast-refresh, or a browser extension's own promise chain.
window.addEventListener("unhandledrejection", (event) => {
  const reason = event?.reason;
  const msg = (typeof reason === "string" ? reason : reason?.message) || "";
  if (/^AbortError|The user aborted a request|Failed to fetch$/i.test(msg)) {
    event.preventDefault();
  }
}, true);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
